/**
 * The grid itself: a pinned client column, a pinned head row, a checkbox per cell and a footer.
 *
 * ── Every cell is a real checkbox with a full name ───────────────────────────────────────────
 * „Przypisz „Check-in tygodniowy" klientowi Maja Kowalska". The `<th scope>` pair already names the
 * row and the column for a reader that follows them, but the platform's support for that in a
 * fifteen-column grid is uneven enough that the label states both outright. They stay NATIVE
 * checkboxes: „checked", Space and the click target are the browser's.
 *
 * ── ONE tab stop, and the arrows move it ─────────────────────────────────────────────────────
 * `shared/lib/useGridFocus`. The canon's own case is eight clients by fifteen surveys: as plain tab
 * stops that is 120 cells plus 15 column heads standing between the tab strip and the note under the
 * card, with no way past them but 135 presses. So the grid holds one stop — the head row is row 0
 * and the clients follow it — Arrows move by a cell, Home and End are the row's ends, Ctrl (or ⌘)
 * with them are the grid's corners, and Page Up / Page Down cross half the columns at a time.
 *
 * It stays a `<table>` rather than taking `role="grid"`, and that is a decision rather than an
 * omission. A grid's every cell should be reachable by the arrows, and three kinds here hold nothing
 * to reach: the corner, the client name in each row header, and the whole footer of tallies. The
 * native table role is also what actually serves a screen reader on a rectangle of 120 facts — it
 * keeps row and column header association and the reader's own table navigation. The roving stop is
 * added on top of that, where it costs nothing and removes 134 presses.
 *
 * ── The tick does NOT move on press ──────────────────────────────────────────────────────────
 * The write is not optimistic (`useMatrixTab`'s header). The cell goes busy and the tick follows the
 * refetched list, so a failed write leaves the checkbox telling the truth rather than lying and then
 * flipping back.
 *
 * ── A cell in a PAUSED column is half refused, and the half is the backend's ────────────────
 * `AssignCheckInAsync` refuses a paused check-in; `UnassignCheckInAsync` does not. So an empty cell
 * in a paused column is `aria-disabled` with a rendered reason an `aria-describedby` points at — it
 * keeps its place in the grid and its explanation with it — while a ticked one is untouched. The
 * decision is `model/matrix`'s `cellRefusal`; nothing about it is decided here.
 *
 * ── The column head is the way the card is pointed ───────────────────────────────────────────
 * Pressing a head describes that survey in the card beside the grid. It is a button inside the
 * `<th>`, not a clickable cell, so it is reachable by keyboard and named.
 */

import { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useGridFocus } from '~/shared/lib/useGridFocus';
import { AvatarChip } from '~/shared/ui/AvatarChip';
import { CheckIcon } from '~/shared/ui/AuthIcons';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import {
  cellRefusal,
  cellRefusalMessage,
  columnTally,
  holdsSurvey,
  type MatrixColumn,
  type MatrixView,
} from '../model/matrix';
import styles from './Matrix.module.css';

interface MatrixTableProps {
  readonly view: MatrixView;
  readonly selectedId: number | null;
  readonly onSelectColumn: (id: number) => void;
  readonly isBusy: (templateId: number, customerId: number) => boolean;
  readonly onToggle: (column: MatrixColumn, customerId: number) => void;
  readonly reveal: string;
}

export function MatrixTable({
  view,
  selectedId,
  onSelectColumn,
  isBusy,
  onToggle,
  reveal,
}: MatrixTableProps) {
  const { t } = useTranslation();
  const tableRef = useRef<HTMLTableElement>(null);
  // One `useId` for every refusal on the grid: 120 cells would otherwise mint 120 identifiers to
  // name the handful of refusals a paused column has. The editor's form takes the same shape.
  const reasonsId = useId();
  // Row 0 is the head row of survey buttons; the clients start at 1.
  const nav = useGridFocus({
    gridRef: tableRef,
    rowCount: view.rows.length + 1,
    columnCount: view.columns.length,
  });

  return (
    <table ref={tableRef} className={`${String(styles.table)} ${reveal}`} onKeyDown={nav.onKeyDown}>
      {/* Named in the accessibility tree and nowhere on the screen: the tab strip already says
        * „Kto ma którą" above it, and a caption printed twice is noise. `hidden` would take it out
        * of the tree with it — see `visuallyHidden.module.css`. */}
      <caption className={hidden.srOnly}>{t('web:reports.matrix.label')}</caption>
      {/* The tracks, declared once. Under `table-layout: fixed` these are the whole of the grid's
        * geometry, which is what stops ONE survey column from taking the card's whole width — see
        * `Matrix.module.css`. Each width is a plain token; a `calc(…)` here reads as `auto`. */}
      <colgroup>
        <col className={styles.colClient} />
        {view.columns.map((column) => (
          <col key={column.id} className={styles.colSurvey} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th
            scope="col"
            className={`${String(styles.headCell)} ${String(styles.headCorner)}`}
          >
            {t('web:reports.matrix.clientColumn')}
          </th>
          {view.columns.map((column, columnIndex) => (
            <th
              key={column.id}
              scope="col"
              className={`${String(styles.headCell)} ${
                column.id === selectedId ? String(styles.headCellOn) : ''
              }`}
            >
              <button
                type="button"
                className={styles.headButton}
                aria-pressed={column.id === selectedId}
                data-cell={`0-${String(columnIndex)}`}
                tabIndex={nav.focus.row === 0 && nav.focus.column === columnIndex ? 0 : -1}
                onFocus={() => {
                  nav.onCellFocus(0, columnIndex);
                }}
                onClick={() => {
                  onSelectColumn(column.id);
                }}
              >
                {/* `title` so the pointer can read a title the ellipsis cut; the accessible name is
                  * the button's own text, which is the whole title plus the cadence. */}
                <span className={styles.headTitle} title={column.title}>
                  {column.title}
                </span>
                <span className={styles.headCadence}>{column.cadence}</span>
              </button>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {view.rows.map((row, rowIndex) => (
          <tr key={row.customerId}>
            <th scope="row" className={styles.rowHead}>
              <span className={styles.person}>
                <AvatarChip initials={row.initials} avatarUrl={row.avatarUrl} loading="lazy" />
                <span className={styles.personName}>{row.name}</span>
              </span>
            </th>
            {view.columns.map((column, columnIndex) => {
              const held = holdsSurvey(column, row.customerId);
              const busy = isBusy(column.id, row.customerId);
              const refusal = cellRefusal(column, row.customerId);
              const refusalId = `${String(reasonsId)}-${String(column.id)}-${String(row.customerId)}`;
              const address = { row: rowIndex + 1, column: columnIndex };
              return (
                <td
                  key={column.id}
                  className={`${String(styles.cell)} ${
                    column.id === selectedId ? String(styles.cellOn) : ''
                  }`}
                >
                  <label
                    className={`${String(styles.check)} ${
                      refusal === null ? '' : String(styles.checkRefused)
                    }`}
                  >
                    <input
                      type="checkbox"
                      className={styles.control}
                      checked={held}
                      aria-busy={busy}
                      // `aria-disabled`, never the attribute: a refused cell stays focusable, stays
                      // reachable by the grid's arrows, and keeps the sentence saying why.
                      aria-disabled={refusal === null ? undefined : true}
                      aria-describedby={refusal === null ? undefined : refusalId}
                      aria-label={t(
                        held ? 'web:reports.matrix.unassign' : 'web:reports.matrix.assign',
                        { survey: column.title, client: row.name },
                      )}
                      data-cell={`${String(address.row)}-${String(address.column)}`}
                      tabIndex={
                        nav.focus.row === address.row && nav.focus.column === address.column ? 0 : -1
                      }
                      onFocus={() => {
                        nav.onCellFocus(address.row, address.column);
                      }}
                      onKeyDown={(event) => {
                        // Space is the browser's on a checkbox; Enter is not bound to anything at
                        // all there, and a trainer who has just arrowed to a cell presses it.
                        if (event.key !== 'Enter') return;
                        event.preventDefault();
                        if (refusal !== null) return;
                        onToggle(column, row.customerId);
                      }}
                      onChange={() => {
                        if (refusal !== null) return;
                        onToggle(column, row.customerId);
                      }}
                    />
                    <span
                      className={[
                        String(styles.box),
                        held ? String(styles.boxOn) : '',
                        busy ? String(styles.boxBusy) : '',
                        refusal === null ? '' : String(styles.boxRefused),
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-hidden
                    >
                      {held ? <CheckIcon className={styles.tick} /> : null}
                    </span>
                    {/* Rendered, never a `title`: the grid has no room for a sentence per cell and a
                      * tooltip reaches neither a touch trainer nor a keyboard one who never hovers. */}
                    {refusal === null ? null : (
                      <span id={refusalId} className={hidden.srOnly}>
                        {cellRefusalMessage(refusal, t)}
                      </span>
                    )}
                  </label>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <th
            scope="row"
            className={`${String(styles.footCell)} ${String(styles.footHead)}`}
          >
            {t('web:reports.matrix.footClients', { count: view.rows.length })}
            {view.intake === null
              ? null
              : ` · ${t('web:reports.matrix.footIntake', {
                  held: view.intake.held,
                  total: view.intake.total,
                })}`}
          </th>
          {view.columns.map((column) => {
            const tally = columnTally(column, view);
            return (
              <td key={column.id} className={styles.footCell}>
                {/* The figure is drawn short and named in full: „3/8" is unreadable on its own to
                  * anyone who cannot see the column it stands under. */}
                <span aria-hidden>
                  {t('web:reports.matrix.footCell', { held: tally.held, total: tally.total })}
                </span>
                <span className={hidden.srOnly}>
                  {t('web:reports.matrix.footCellLabel', {
                    title: column.title,
                    held: tally.held,
                    total: tally.total,
                  })}
                </span>
              </td>
            );
          })}
        </tr>
      </tfoot>
    </table>
  );
}
